/** @jsx jsx */

import type { CSSProperties, ReactNode } from 'react';
import { forwardRef, useMemo } from 'react';
import { buildDataAttributes, jsx } from '@balance-web/core';
import {
  DefaultTextPropsProvider,
  useDefaultTextProps,
} from '@balance-web/text';
import { useTheme } from '@balance-web/theme';

import { TableRowContext } from '../rowContext';
import { siftAriaAttributes } from '../utils';

// Component
// ------------------------------

export type TableRowProps = UseRowStylesProps & {
  /** The elements that make up the table row. */
  children: ReactNode;
  /**
   * When only a subset of rows are loaded, for virtualization or pagination,
   * use the aria-rowindex attribute to let assistive technologies know what
   * portions of the content are being displayed, and that all the table's
   * content is not present.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-rowindex
   */
  'aria-rowindex'?: number;
};

/**
 * `tone` is the row's own vocabulary, so it is mapped to a text colour here
 * rather than renamed to follow the `Text` `color` prop (RWR-18028).
 */
const TONE_COLORS = {
  critical: 'critical',
  positive: 'success',
  cautious: 'warning',
} as const;

export const TableRow = forwardRef<HTMLDivElement, TableRowProps>(
  ({ children, alignY, ...props }, ref) => {
    const defaultTextProps = useDefaultTextProps({
      color: props.tone ? TONE_COLORS[props.tone] : undefined,
    });
    const { css, dataAttributes, style } = useRowStyles(props);
    const ariaAttributes = siftAriaAttributes(props);

    return (
      <DefaultTextPropsProvider {...defaultTextProps}>
        <TableRowContext.Provider value={{ alignY }}>
          <div
            ref={ref}
            css={css}
            role="row"
            style={style}
            {...ariaAttributes}
            {...dataAttributes}
          >
            {children}
          </div>
        </TableRowContext.Provider>
      </DefaultTextPropsProvider>
    );
  }
);

// Styles
// ------------------------------

type UseRowStylesProps = {
  /**
   * For vertical alignemtn of all cell content in this row.
   * @default center
   */
  alignY?: 'top' | 'center' | 'bottom';
  /** Sets the interaction status. */
  status?: 'dragging';
  /** Sets the background colour for the row. */
  tone?: 'critical' | 'positive' | 'cautious';
  disabled?: boolean;
  /**
   * Sets inline [style](https://reactjs.org/docs/dom-elements.html#style) for
   * the element. Only use as a **last resort**.
   */
  UNSAFE_style?: CSSProperties;
};

export function useRowStyles(props: UseRowStylesProps) {
  const { shadow } = useTheme();
  const { disabled, status, tone, UNSAFE_style } = props;

  const style = useMemo(() => {
    return UNSAFE_style;
  }, [UNSAFE_style]);
  const css = useMemo(() => {
    return [
      {
        cursor: 'default',
        display: 'flex',
      },
      {
        '[data-row-status="dragging"]&': {
          boxShadow: shadow.small,
        },
      },
    ];
  }, [shadow]);
  const dataAttributes = useMemo(() => {
    return buildDataAttributes({ status, disabled, tone }, 'row');
  }, [status, tone, disabled]);

  return { css, dataAttributes, style };
}
